import { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Group,
  Loader,
  ScrollArea,
  SegmentedControl,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core'
import {
  IconChevronDown,
  IconChevronRight,
  IconFolder,
} from '@tabler/icons-react'
import { api } from '@/api/client'
import { type FileItem, type FileListResponse, ALLOWED_ROOTS } from './types'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TreeNode {
  name: string
  path: string
  expanded: boolean
  loading: boolean
  children: TreeNode[] | null // null = not yet fetched
}

export interface FileTreeProps {
  tenantId: number
  instanceId: string
  onSelect: (path: string) => void
}

// ---------------------------------------------------------------------------
// Pure tree-mutation helpers
// ---------------------------------------------------------------------------

function markLoading(
  nodes: TreeNode[],
  targetPath: string,
  loading: boolean,
): TreeNode[] {
  return nodes.map((n) => {
    if (n.path === targetPath) return { ...n, loading }
    if (n.children) return { ...n, children: markLoading(n.children, targetPath, loading) }
    return n
  })
}

function insertChildren(
  nodes: TreeNode[],
  targetPath: string,
  children: TreeNode[],
): TreeNode[] {
  return nodes.map((n) => {
    if (n.path === targetPath) return { ...n, children, loading: false, expanded: true }
    if (n.children) return { ...n, children: insertChildren(n.children, targetPath, children) }
    return n
  })
}

function toggleExpanded(nodes: TreeNode[], targetPath: string): TreeNode[] {
  return nodes.map((n) => {
    if (n.path === targetPath) return { ...n, expanded: !n.expanded }
    if (n.children) return { ...n, children: toggleExpanded(n.children, targetPath) }
    return n
  })
}

function itemsToNodes(items: FileItem[], parentPath: string): TreeNode[] {
  const base = parentPath.replace(/\/$/, '')
  return items
    .filter((i) => i.type === 'directory' && i.name !== '.' && i.name !== '..')
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((i) => ({
      name: i.name,
      path: `${base}/${i.name}`,
      expanded: false,
      loading: false,
      children: null,
    }))
}

// ---------------------------------------------------------------------------
// Root labels
// ---------------------------------------------------------------------------

const ROOT_LABELS: Record<string, string> = {
  [ALLOWED_ROOTS[0]]: 'ubuntu',
  [ALLOWED_ROOTS[1]]: 'log',
}

const SEGMENTS = ALLOWED_ROOTS.map((r) => ({ label: ROOT_LABELS[r], value: r }))

// ---------------------------------------------------------------------------
// Recursive renderer
// ---------------------------------------------------------------------------

interface RenderNodesProps {
  nodes: TreeNode[]
  depth: number
  selectedPath: string | null
  onExpand: (node: TreeNode) => void
  onSelect: (path: string) => void
}

function RenderNodes({ nodes, depth, selectedPath, onExpand, onSelect }: RenderNodesProps) {
  return (
    <>
      {nodes.map((node) => {
        const isSelected = node.path === selectedPath
        return (
          <Box key={node.path}>
            <UnstyledButton
              w="100%"
              py={3}
              px={4}
              style={{
                paddingLeft: depth * 16 + 4,
                borderRadius: 4,
                background: isSelected ? 'var(--mantine-color-blue-9)' : undefined,
                transition: 'background 0.1s',
              }}
              onMouseEnter={(e) => {
                if (!isSelected) (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.12)'
              }}
              onMouseLeave={(e) => {
                if (!isSelected) (e.currentTarget as HTMLElement).style.background = ''
              }}
              onClick={() => {
                onSelect(node.path)
                onExpand(node)
              }}
              data-testid={`tree-node-${node.path}`}
            >
              <Group gap={4} wrap="nowrap">
                {node.loading ? (
                  <Loader size={14} />
                ) : node.expanded && node.children ? (
                  <IconChevronDown size={14} color={isSelected ? 'var(--mantine-color-blue-4)' : undefined} />
                ) : (
                  <IconChevronRight size={14} color={isSelected ? 'var(--mantine-color-blue-4)' : 'var(--mantine-color-dark-2)'} />
                )}
                <IconFolder size={14} color={isSelected ? 'var(--mantine-color-blue-4)' : 'var(--mantine-color-yellow-6)'} />
                <Text size="sm" truncate c={isSelected ? 'blue.2' : undefined} fw={isSelected ? 600 : undefined}>
                  {node.name}
                </Text>
              </Group>
            </UnstyledButton>

            {node.expanded && node.children && (
              <Box style={{ borderLeft: '1px solid var(--mantine-color-dark-4)', marginLeft: depth * 16 + 14 }}>
                <RenderNodes
                  nodes={node.children}
                  depth={0}
                  selectedPath={selectedPath}
                  onExpand={onExpand}
                  onSelect={onSelect}
                />
              </Box>
            )}
          </Box>
        )
      })}
    </>
  )
}

// ---------------------------------------------------------------------------
// FileTree component
// ---------------------------------------------------------------------------

export function FileTree({ tenantId, instanceId, onSelect }: FileTreeProps) {
  const [activeRoot, setActiveRoot] = useState<string>(ALLOWED_ROOTS[0])
  const [nodes, setNodes] = useState<Record<string, TreeNode[]>>({})
  const [selectedPath, setSelectedPath] = useState<string | null>(null)

  const fetchDir = useCallback(
    async (path: string): Promise<TreeNode[]> => {
      const res = await api.get<FileListResponse>(
        `/v1/tenants/${tenantId}/inventory/ec2/${instanceId}/files`,
        { params: { path } },
      )
      return itemsToNodes(res.data.items, path)
    },
    [tenantId, instanceId],
  )

  const ensureRoot = useCallback(
    async (root: string) => {
      if (nodes[root]) return // already loaded
      const children = await fetchDir(root)
      setNodes((prev) => ({ ...prev, [root]: children }))
    },
    [nodes, fetchDir],
  )

  const handleExpand = useCallback(
    async (node: TreeNode) => {
      // Children already loaded — just toggle
      if (node.children !== null) {
        setNodes((prev) => ({
          ...prev,
          [activeRoot]: toggleExpanded(prev[activeRoot], node.path),
        }))
        return
      }

      // Mark loading
      setNodes((prev) => ({
        ...prev,
        [activeRoot]: markLoading(prev[activeRoot], node.path, true),
      }))

      const children = await fetchDir(node.path)

      setNodes((prev) => ({
        ...prev,
        [activeRoot]: insertChildren(prev[activeRoot], node.path, children),
      }))
    },
    [activeRoot, fetchDir],
  )

  const handleSelect = useCallback(
    (path: string) => {
      setSelectedPath(path)
      onSelect(path)
    },
    [onSelect],
  )

  // Load root directories on activeRoot change
  useEffect(() => {
    ensureRoot(activeRoot)
  }, [activeRoot, ensureRoot])

  const currentNodes = nodes[activeRoot] ?? []

  return (
    <Stack gap={0} h="100%">
      <Box p="xs" style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
        <SegmentedControl
          data={SEGMENTS}
          value={activeRoot}
          onChange={(v) => { setActiveRoot(v); setSelectedPath(v); onSelect(v) }}
          size="xs"
          fullWidth
        />
      </Box>
      <ScrollArea style={{ flex: 1 }} p="xs">
        {currentNodes.length === 0 ? (
          <Group justify="center" py="md">
            <Loader size="sm" />
          </Group>
        ) : (
          <RenderNodes
            nodes={currentNodes}
            depth={0}
            selectedPath={selectedPath}
            onExpand={handleExpand}
            onSelect={handleSelect}
          />
        )}
      </ScrollArea>
    </Stack>
  )
}
